import {
  ArrowDownTrayIcon,
  ArrowLeftCircleIcon,
  ArrowLeftEndOnRectangleIcon,
  ArrowLeftIcon,
  ArrowLongLeftIcon,
  ArrowPathIcon,
  ArrowRightCircleIcon,
  ArrowRightIcon,
  ArrowsRightLeftIcon,
  ArrowUpTrayIcon,
  Bars3Icon,
  CalendarDaysIcon,
  CalendarIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ClockIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  GlobeAmericasIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PhotoIcon,
  TrashIcon,
  UserIcon,
  UserPlusIcon,
  VideoCameraIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon as CheckCircleIconSolid,
  ExclamationCircleIcon as ExclamationCircleIconSolid,
  XCircleIcon as XCircleIconSolid,
  XMarkIcon as XMarkIconSolid,
} from "@heroicons/react/24/solid";

import type { SVGProps } from "~/shared.types";
import { tw } from "~/utils";

export const icons = {
  outline: {
    CheckCircleIcon,
    XCircleIcon,
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    ArrowLeftCircleIcon,
    ArrowLeftEndOnRectangleIcon,
    ArrowLeftIcon,
    ArrowLongLeftIcon,
    ArrowPathIcon,
    ArrowRightCircleIcon,
    ArrowRightIcon,
    ArrowsRightLeftIcon,
    Bars3Icon,
    CalendarDaysIcon,
    CalendarIcon,
    CheckIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronUpDownIcon,
    ChevronUpIcon,
    ClockIcon,
    Cog6ToothIcon,
    DocumentTextIcon,
    EllipsisHorizontalIcon,
    ExclamationCircleIcon,
    EyeIcon,
    EyeSlashIcon,
    GlobeAmericasIcon,
    InformationCircleIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    PhotoIcon,
    TrashIcon,
    UserIcon,
    UserPlusIcon,
    VideoCameraIcon,
    XMarkIcon,
  },

  solid: {
    CheckCircleIcon: CheckCircleIconSolid,
    ExclamationCircleIcon: ExclamationCircleIconSolid,
    XCircleIcon: XCircleIconSolid,
    XMarkIcon: XMarkIconSolid,
  },
};

export const customIcons = {
  SpinnerIcon: ({ className, ...props }: SVGProps) => (
    <div role="status">
      <svg
        aria-hidden="true"
        className={tw(
          "mr-2 h-8 w-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600",
          className,
        )}
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
      <span className="sr-only">Loading...</span>
    </div>
  ),

  LogoIcon: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      viewBox="0 0 230 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Elektra Health Logo"
      role="img"
      {...props}
    >
      <title>Elektra Health Logo</title>
      <path
        d="M67.0784 30.3309C67.0784 30.5298 67.2375 30.7088 67.4563 30.7088H75.5514C75.7702 30.7088 75.9293 30.5298 75.9293 30.3309V28.6997C75.9293 28.5008 75.7702 28.3218 75.5514 28.3218H69.664V24.8407H74.5768C74.7757 24.8407 74.9547 24.6815 74.9547 24.4627V22.8117C74.9547 22.6128 74.7757 22.4338 74.5768 22.4338H69.664V19.1715H75.5514C75.7702 19.1715 75.9293 18.9924 75.9293 18.7935V17.1624C75.9293 16.9635 75.7702 16.7844 75.5514 16.7844H67.4563C67.2375 16.7844 67.0784 16.9635 67.0784 17.1624V30.3309Z"
        fill="#5420D8"
      />
      <path
        d="M79.1266 30.3309C79.1266 30.5298 79.2857 30.7088 79.5045 30.7088H86.7842C87.0029 30.7088 87.1621 30.5298 87.1621 30.3309V28.6997C87.1621 28.5008 87.0029 28.3218 86.7842 28.3218H81.7123V17.1624C81.7123 16.9635 81.5332 16.7844 81.3344 16.7844H79.5045C79.2857 16.7844 79.1266 16.9635 79.1266 17.1624V30.3309Z"
        fill="#5420D8"
      />
      <path
        d="M89.5238 30.3309C89.5238 30.5298 89.6829 30.7088 89.9017 30.7088H97.9968C98.2156 30.7088 98.3747 30.5298 98.3747 30.3309V28.6997C98.3747 28.5008 98.2156 28.3218 97.9968 28.3218H92.1094V24.8407H97.0222C97.2211 24.8407 97.4001 24.6815 97.4001 24.4627V22.8117C97.4001 22.6128 97.2211 22.4338 97.0222 22.4338H92.1094V19.1715H97.9968C98.2156 19.1715 98.3747 18.9924 98.3747 18.7935V17.1624C98.3747 16.9635 98.2156 16.7844 97.9968 16.7844H89.9017C89.6829 16.7844 89.5238 16.9635 89.5238 17.1624V30.3309Z"
        fill="#5420D8"
      />
      <path
        d="M101.572 30.2314C101.572 30.49 101.771 30.7088 102.049 30.7088H103.8C104.058 30.7088 104.277 30.49 104.277 30.2314V24.1644L109.468 30.5696C109.508 30.6292 109.627 30.7088 109.826 30.7088H111.994C112.392 30.7088 112.551 30.2712 112.352 30.0126L106.783 23.3488L111.974 17.5006C112.233 17.2022 112.034 16.7844 111.656 16.7844H109.667C109.488 16.7844 109.369 16.8839 109.289 16.9834L104.277 22.6327V17.2618C104.277 17.0033 104.058 16.7844 103.8 16.7844H102.049C101.771 16.7844 101.572 17.0033 101.572 17.2618V30.2314Z"
        fill="#5420D8"
      />
      <path
        d="M116.83 30.3309C116.83 30.5298 117.009 30.7088 117.208 30.7088H119.058C119.257 30.7088 119.436 30.5298 119.436 30.3309V19.1715H122.479C122.698 19.1715 122.857 18.9924 122.857 18.7935V17.1624C122.857 16.9635 122.698 16.7844 122.479 16.7844H113.787C113.568 16.7844 113.409 16.9635 113.409 17.1624V18.7935C113.409 18.9924 113.568 19.1715 113.787 19.1715H116.83V30.3309Z"
        fill="#5420D8"
      />
      <path
        d="M125.28 30.3309C125.28 30.5298 125.439 30.7088 125.658 30.7088H127.488C127.687 30.7088 127.866 30.5298 127.866 30.3309V25.2584H130.073L132.719 30.5298C132.758 30.6093 132.858 30.7088 133.037 30.7088H135.125C135.464 30.7088 135.603 30.3905 135.464 30.1319L132.739 25.0794C134.469 24.3832 135.682 22.8913 135.682 21.0612C135.682 18.6941 133.733 16.7844 131.346 16.7844H125.658C125.439 16.7844 125.28 16.9635 125.28 17.1624V30.3309ZM127.885 23.13V19.1715H131.128C132.182 19.1715 133.097 20.0467 133.097 21.101C133.097 22.2149 132.182 23.13 131.128 23.13H127.885Z"
        fill="#5420D8"
      />
      <path
        d="M137.41 30.7088H139.141C139.459 30.7088 139.638 30.5099 139.717 30.311L140.692 28.1626H146.639L147.614 30.311C147.753 30.5895 147.892 30.7088 148.19 30.7088H149.921C150.219 30.7088 150.378 30.4502 150.259 30.1916L144.133 16.8043C144.073 16.685 143.994 16.5855 143.795 16.5855H143.596C143.417 16.5855 143.317 16.685 143.258 16.8043L137.072 30.1916C136.953 30.4502 137.112 30.7088 137.41 30.7088ZM141.667 25.9546L143.616 21.5784H143.675L145.664 25.9546H141.667Z"
        fill="#5420D8"
      />
      <path
        d="M157.171 30.3309C157.171 30.5298 157.35 30.7088 157.549 30.7088H159.398C159.617 30.7088 159.776 30.5298 159.776 30.3309V24.8407H166.26V30.3309C166.26 30.5298 166.42 30.7088 166.638 30.7088H168.468C168.667 30.7088 168.846 30.5298 168.846 30.3309V17.1624C168.846 16.9635 168.667 16.7844 168.468 16.7844H166.638C166.42 16.7844 166.26 16.9635 166.26 17.1624V22.4338H159.776V17.1624C159.776 16.9635 159.617 16.7844 159.398 16.7844H157.549C157.35 16.7844 157.171 16.9635 157.171 17.1624V30.3309Z"
        fill="#5420D8"
      />
      <path
        d="M172.812 30.3309C172.812 30.5298 172.971 30.7088 173.19 30.7088H181.285C181.504 30.7088 181.663 30.5298 181.663 30.3309V28.6997C181.663 28.5008 181.504 28.3218 181.285 28.3218H175.398V24.8407H180.311C180.51 24.8407 180.689 24.6815 180.689 24.4627V22.8117C180.689 22.6128 180.51 22.4338 180.311 22.4338H175.398V19.1715H181.285C181.504 19.1715 181.663 18.9924 181.663 18.7935V17.1624C181.663 16.9635 181.504 16.7844 181.285 16.7844H173.19C172.971 16.7844 172.812 16.9635 172.812 17.1624V30.3309Z"
        fill="#5420D8"
      />
      <path
        d="M183.389 30.7088H185.119C185.437 30.7088 185.616 30.5099 185.696 30.311L186.671 28.1626H192.618L193.592 30.311C193.731 30.5895 193.871 30.7088 194.169 30.7088H195.899C196.198 30.7088 196.357 30.4502 196.238 30.1916L190.111 16.8043C190.052 16.685 189.972 16.5855 189.773 16.5855H189.574C189.395 16.5855 189.296 16.685 189.236 16.8043L183.051 30.1916C182.931 30.4502 183.09 30.7088 183.389 30.7088ZM187.645 25.9546L189.594 21.5784H189.654L191.643 25.9546H187.645Z"
        fill="#5420D8"
      />
      <path
        d="M198.404 30.3309C198.404 30.5298 198.563 30.7088 198.782 30.7088H206.062C206.281 30.7088 206.44 30.5298 206.44 30.3309V28.6997C206.44 28.5008 206.281 28.3218 206.062 28.3218H200.99V17.1624C200.99 16.9635 200.811 16.7844 200.612 16.7844H198.782C198.563 16.7844 198.404 16.9635 198.404 17.1624V30.3309Z"
        fill="#5420D8"
      />
      <path
        d="M209.875 30.3309C209.875 30.5298 210.054 30.7088 210.253 30.7088H212.102C212.301 30.7088 212.48 30.5298 212.48 30.3309V19.1715H215.524C215.742 19.1715 215.901 18.9924 215.901 18.7935V17.1624C215.901 16.9635 215.742 16.7844 215.524 16.7844H206.832C206.613 16.7844 206.454 16.9635 206.454 17.1624V18.7935C206.454 18.9924 206.613 19.1715 206.832 19.1715H209.875V30.3309Z"
        fill="#5420D8"
      />
      <path
        d="M218.325 30.3309C218.325 30.5298 218.504 30.7088 218.703 30.7088H220.552C220.771 30.7088 220.93 30.5298 220.93 30.3309V24.8407H227.414V30.3309C227.414 30.5298 227.573 30.7088 227.792 30.7088H229.622C229.821 30.7088 230 30.5298 230 30.3309V17.1624C230 16.9635 229.821 16.7844 229.622 16.7844H227.792C227.573 16.7844 227.414 16.9635 227.414 17.1624V22.4338H220.93V17.1624C220.93 16.9635 220.771 16.7844 220.552 16.7844H218.703C218.504 16.7844 218.325 16.9635 218.325 17.1624V30.3309Z"
        fill="#5420D8"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M23.654 47.6567C36.7177 47.6567 47.3079 37.0654 47.3079 24.0002C47.3079 10.9351 36.7177 0.34375 23.654 0.34375C10.5902 0.34375 0 10.9351 0 24.0002C0 37.0654 10.5902 47.6567 23.654 47.6567ZM19.3032 31.3928C19.043 31.3928 18.8538 31.1798 18.8538 30.9433V15.2827C18.8538 15.0461 19.043 14.8332 19.3032 14.8332H28.9303C29.1905 14.8332 29.3798 15.0461 29.3798 15.2827V17.2225C29.3798 17.4591 29.1905 17.672 28.9303 17.672H21.9288V21.5517H27.7713C28.0078 21.5517 28.2207 21.7646 28.2207 22.0011V23.9646C28.2207 24.2248 28.0078 24.4141 27.7713 24.4141H21.9288V28.554H28.9303C29.1905 28.554 29.3798 28.7669 29.3798 29.0035V30.9433C29.3798 31.1798 29.1905 31.3928 28.9303 31.3928H19.3032Z"
        fill="#5420D8"
      />
    </svg>
  ),

  SearchIcon: ({ className, ...props }: SVGProps) => (
    <svg
      className={tw("w-8", className)}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g opacity="0.5" clipPath="url(#clip0_2322_3442)">
        <path
          d="M20.3384 18.6667H19.3018L18.9344 18.3067C20.2203 16.7867 20.9944 14.8133 20.9944 12.6667C20.9944 7.88 17.1763 4 12.4659 4C7.75562 4 3.9375 7.88 3.9375 12.6667C3.9375 17.4533 7.75562 21.3333 12.4659 21.3333C14.5784 21.3333 16.5202 20.5467 18.016 19.24L18.3702 19.6133V20.6667L24.9306 27.32L26.8856 25.3333L20.3384 18.6667ZM12.4659 18.6667C9.19889 18.6667 6.56164 15.9867 6.56164 12.6667C6.56164 9.34667 9.19889 6.66667 12.4659 6.66667C15.733 6.66667 18.3702 9.34667 18.3702 12.6667C18.3702 15.9867 15.733 18.6667 12.4659 18.6667Z"
          fill="#000066"
        />
      </g>
      <defs>
        <clipPath id="clip0_2322_3442">
          <rect width="31.4896" height="32" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),

  UncheckedCheckbox: ({ className, ...props }: SVGProps) => (
    <svg
      className={tw("w-4", className)}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M0.301653 2.41323C0.301653 1.24704 1.24704 0.301653 2.41323 0.301653H13.5868C14.753 0.301653 15.6983 1.24704 15.6983 2.41323V13.5868C15.6983 14.753 14.753 15.6983 13.5868 15.6983H2.41323C1.24704 15.6983 0.301653 14.753 0.301653 13.5868V2.41323Z"
        fill="white"
        stroke="#94A3B8"
        strokeWidth="0.603307"
      />
    </svg>
  ),

  CheckedCheckbox: ({ className, ...props }: SVGProps) => (
    <svg
      className={tw("w-4", className)}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M0.301659 4C0.301659 1.95746 1.95746 0.301659 4 0.301659H12C14.0425 0.301659 15.6983 1.95746 15.6983 4V12C15.6983 14.0425 14.0425 15.6983 12 15.6983H4C1.95746 15.6983 0.301659 14.0425 0.301659 12V4Z"
        fill="#333E59"
      />
      <path
        d="M0.301659 4C0.301659 1.95746 1.95746 0.301659 4 0.301659H12C14.0425 0.301659 15.6983 1.95746 15.6983 4V12C15.6983 14.0425 14.0425 15.6983 12 15.6983H4C1.95746 15.6983 0.301659 14.0425 0.301659 12V4Z"
        stroke="#94A3B8"
        strokeWidth="0.603318"
      />
      <path
        d="M12.926 5.75955L6.89104 11.7946C6.83848 11.8473 6.77602 11.8891 6.70725 11.9177C6.63848 11.9463 6.56474 11.961 6.49028 11.961C6.41581 11.961 6.34208 11.9463 6.27331 11.9177C6.20454 11.8891 6.14208 11.8473 6.08952 11.7946L3.4492 9.15424C3.39658 9.10161 3.35483 9.03913 3.32635 8.97037C3.29786 8.9016 3.2832 8.8279 3.2832 8.75348C3.2832 8.67905 3.29786 8.60535 3.32635 8.53659C3.35483 8.46782 3.39658 8.40534 3.4492 8.35271C3.50183 8.30009 3.56431 8.25834 3.63307 8.22986C3.70184 8.20137 3.77554 8.18671 3.84997 8.18671C3.92439 8.18671 3.99809 8.20137 4.06686 8.22986C4.13562 8.25834 4.1981 8.30009 4.25073 8.35271L6.49075 10.5927L12.1255 4.95897C12.2317 4.85268 12.3759 4.79297 12.5262 4.79297C12.6765 4.79297 12.8207 4.85268 12.927 4.95897C13.0333 5.06526 13.093 5.20942 13.093 5.35973C13.093 5.51005 13.0333 5.6542 12.927 5.76049L12.926 5.75955Z"
        fill="#EAECF4"
      />
    </svg>
  ),

  LogoutIcon: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      {...props}
      viewBox="0 0 26 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M9.75091 18C9.75091 18.221 9.8365 18.433 9.98887 18.5893C10.1412 18.7455 10.3479 18.8333 10.5634 18.8333H23.5626C23.7781 18.8333 23.9848 18.7455 24.1371 18.5893C24.2895 18.433 24.3751 18.221 24.3751 18V3C24.3751 2.77899 24.2895 2.56702 24.1371 2.41074C23.9848 2.25446 23.7781 2.16667 23.5626 2.16667H10.5634C10.3479 2.16667 10.1412 2.25446 9.98887 2.41074C9.8365 2.56702 9.75091 2.77899 9.75091 3V6.33333C9.75091 6.55435 9.66531 6.76631 9.51294 6.92259C9.36058 7.07887 9.15393 7.16667 8.93845 7.16667C8.72298 7.16667 8.51632 7.07887 8.36396 6.92259C8.2116 6.76631 8.126 6.55435 8.126 6.33333V3C8.126 2.33696 8.38279 1.70107 8.83988 1.23223C9.29698 0.763392 9.91693 0.5 10.5634 0.5H23.5626C24.2091 0.5 24.829 0.763392 25.2861 1.23223C25.7432 1.70107 26 2.33696 26 3V18C26 18.663 25.7432 19.2989 25.2861 19.7678C24.829 20.2366 24.2091 20.5 23.5626 20.5H10.5634C9.91693 20.5 9.29698 20.2366 8.83988 19.7678C8.38279 19.2989 8.126 18.663 8.126 18V14.6667C8.126 14.4457 8.2116 14.2337 8.36396 14.0774C8.51632 13.9211 8.72298 13.8333 8.93845 13.8333C9.15393 13.8333 9.36058 13.9211 9.51294 14.0774C9.66531 14.2337 9.75091 14.4457 9.75091 14.6667V18Z"
        fill="#000066"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M0.238689 11.09C0.163028 11.0126 0.102999 10.9206 0.0620411 10.8194C0.0210829 10.7181 0 10.6096 0 10.5C0 10.3904 0.0210829 10.2819 0.0620411 10.1806C0.102999 10.0794 0.163028 9.98741 0.238689 9.91L5.11342 4.91C5.18896 4.83252 5.27863 4.77106 5.37733 4.72913C5.47603 4.6872 5.58181 4.66561 5.68863 4.66561C5.79546 4.66561 5.90124 4.6872 5.99994 4.72913C6.09864 4.77106 6.18831 4.83252 6.26385 4.91C6.33939 4.98748 6.39931 5.07946 6.44019 5.18069C6.48107 5.28193 6.50212 5.39043 6.50212 5.5C6.50212 5.60957 6.48107 5.71807 6.44019 5.81931C6.39931 5.92054 6.33939 6.01252 6.26385 6.09L2.77517 9.66667H17.063C17.2785 9.66667 17.4851 9.75446 17.6375 9.91074C17.7899 10.067 17.8755 10.279 17.8755 10.5C17.8755 10.721 17.7899 10.933 17.6375 11.0893C17.4851 11.2455 17.2785 11.3333 17.063 11.3333H2.77517L6.26385 14.91C6.33939 14.9875 6.39931 15.0795 6.44019 15.1807C6.48107 15.2819 6.50212 15.3904 6.50212 15.5C6.50212 15.6096 6.48107 15.7181 6.44019 15.8193C6.39931 15.9205 6.33939 16.0125 6.26385 16.09C6.18831 16.1675 6.09864 16.2289 5.99994 16.2709C5.90124 16.3128 5.79546 16.3344 5.68863 16.3344C5.58181 16.3344 5.47603 16.3128 5.37733 16.2709C5.27863 16.2289 5.18896 16.1675 5.11342 16.09L0.238689 11.09Z"
        fill="#000066"
      />
    </svg>
  ),

  ArrowUpRightIcon: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      {...props}
      width="29"
      height="29"
      viewBox="0 0 29 29"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.1898 11.6227L8.15625 22.6563L6.34375 20.8437L17.3773 9.81016L7.22734 9.81016L7.25 7.25H21.75V21.75L19.1898 21.7727L19.1898 11.6227Z"
        fill="currentColor"
      />
    </svg>
  ),

  Calendar: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      width="33"
      height="35"
      viewBox="0 0 33 35"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M30.4237 13.9604V7.42182C30.4237 6.55475 30.0792 5.72319 29.4661 5.11008C28.853 4.49697 28.0215 4.15253 27.1544 4.15253H4.2693C3.40223 4.15253 2.57067 4.49697 1.95755 5.11008C1.34444 5.72319 1 6.55475 1 7.42182V13.9604H30.4237Z"
        fill="white"
      />
      <path
        d="M15.7118 33.5762H4.2693C2.47118 33.5762 1 32.105 1 30.3069V13.9604M1 13.9604V7.42182C1 6.55475 1.34444 5.72319 1.95755 5.11008C2.57067 4.49697 3.40223 4.15253 4.2693 4.15253H27.1544C28.0215 4.15253 28.853 4.49697 29.4661 5.11008C30.0792 5.72319 30.4237 6.55475 30.4237 7.42182V13.9604H1Z"
        stroke="currentColor"
        strokeWidth="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M22.017 1V7.30507"
        stroke="currentColor"
        strokeWidth="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M9.40674 1V7.30507"
        stroke="currentColor"
        strokeWidth="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M1 14.1356H30.4237"
        stroke="currentColor"
        strokeWidth="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M30.836 21.6013C30.2789 21.045 29.57 20.6671 28.7988 20.5153C28.0277 20.3634 27.229 20.4445 26.5038 20.7482C26.0133 20.9451 25.5719 21.2404 25.1959 21.6177L24.6401 22.1754L24.0679 21.6177C23.5127 21.0587 22.8046 20.6777 22.0334 20.5229C21.2622 20.3681 20.4625 20.4466 19.7357 20.7482C19.2453 20.9451 18.8203 21.2404 18.4443 21.6177C16.8912 23.1596 16.8095 25.7679 18.7712 27.7528L24.6401 33.5762L30.5254 27.7528C32.4871 25.7679 32.389 23.1596 30.836 21.6177V21.6013Z"
        fill="white"
        stroke="#5420D8"
        strokeWidth="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  ),

  Meet: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      width="48"
      height="31"
      viewBox="0 0 48 31"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M38.0938 21.7687C39.7148 20.4806 40.7955 18.5055 40.7955 16.3158C40.7955 12.4086 37.4634 9.23132 33.3658 9.23132C29.2683 9.23132 25.9362 12.4515 25.9362 16.3587C25.9362 18.4626 26.9268 20.3518 28.4578 21.6399C27.8724 21.9405 27.3321 22.3269 26.8368 22.7562C24.5403 24.6884 23.2345 27.5222 23.2345 30.5277C23.2345 30.7853 23.4146 30.9571 23.6848 30.9571C23.955 30.9571 24.1351 30.7853 24.1351 30.5277C24.1351 27.7368 25.3508 25.1607 27.4222 23.4003C27.9174 22.9709 28.4578 22.6274 29.0432 22.3269C29.0882 22.2839 29.1332 22.2839 29.1783 22.241C30.394 23.0138 31.8349 23.4862 33.3658 23.4862C34.8067 23.4862 36.1576 23.0997 37.3283 22.4128C40.3002 24.0014 42.1463 27.1357 42.1463 30.5706C42.1463 30.8283 42.3265 31 42.5966 31C42.8668 31 43.0469 30.8283 43.0469 30.5706C43.0469 26.8781 41.1557 23.572 38.0938 21.7687ZM38.0938 16.3587C38.0488 16.3587 38.0488 16.3587 38.0938 16.3587V16.3587ZM26.8368 16.5305C27.4672 16.4017 29.8086 15.8006 31.2045 13.5249C32.1951 14.7271 34.8968 17.2604 39.8049 17.3892C39.2645 20.3089 36.6079 22.5845 33.3658 22.5845C29.8086 22.5845 26.9268 19.8795 26.8368 16.5305ZM35.5723 19.9225C35.5723 20.0083 34.9869 21.5111 33.3658 21.5111C31.7448 21.5111 31.1595 20.0083 31.1595 19.9225C31.0694 19.7078 31.2045 19.4501 31.4297 19.3643C31.6548 19.2784 31.9249 19.4072 32.015 19.6219C32.015 19.6648 32.3752 20.6094 33.4109 20.6094C34.4465 20.6094 34.7617 19.6648 34.8068 19.6219C34.8968 19.4072 35.1219 19.2784 35.3921 19.3643C35.5272 19.4501 35.6623 19.7078 35.5723 19.9225ZM24.8105 22.3698C24.9006 22.4986 25.0357 22.5845 25.2157 22.5845C25.3058 22.5845 25.3959 22.5845 25.4409 22.5415C25.666 22.4128 25.7111 22.1551 25.576 21.9405C24.7655 20.6523 23.6848 19.5789 22.379 18.7632C24 17.4751 25.0357 15.5 25.0357 13.3532C25.0357 9.44599 21.7036 6.26868 17.606 6.26868C13.5084 6.26868 10.1763 9.44599 10.1763 13.3532C10.1763 15.4571 11.167 17.3463 12.6979 18.6343C9.50093 20.3948 7.47469 23.7867 7.47469 27.5222C7.47469 27.7798 7.65476 27.9515 7.92495 27.9515C8.19513 27.9515 8.37526 27.7798 8.37526 27.5222C8.37526 24.0014 10.3565 20.7812 13.4634 19.2355C14.6342 20.0083 16.0751 20.4377 17.606 20.4377C19.0919 20.4377 20.4428 20.0083 21.6135 19.3213L21.6585 19.3643C22.9193 20.0512 24 21.1246 24.8105 22.3698ZM22.334 13.3532C22.2889 13.3532 22.2889 13.3532 22.334 13.3532V13.3532ZM11.0769 13.5249C11.7073 13.3961 14.0488 12.795 15.4447 10.5194C16.4352 11.7216 19.137 14.2549 24.045 14.3837C23.5047 17.3033 20.848 19.5789 17.606 19.5789C14.0488 19.5789 11.167 16.874 11.0769 13.5249ZM22.8292 4.63712C25.3508 5.28116 25.8011 5.71053 26.4766 8.11494C26.5216 8.28668 26.7017 8.4155 26.9268 8.4155C27.152 8.4155 27.3321 8.28668 27.3771 8.11494C28.0525 5.71053 28.5028 5.28116 31.0244 4.63712C31.2045 4.59418 31.3396 4.42244 31.3396 4.20775C31.3396 3.99308 31.2045 3.82133 31.0244 3.77839C28.5028 3.13435 28.0525 2.70499 27.3771 0.300554C27.3321 0.128809 27.152 0 26.9268 0C26.7017 0 26.5216 0.128809 26.4766 0.300554C25.8011 2.70499 25.3508 3.13435 22.8292 3.77839C22.6492 3.82133 22.5141 3.99308 22.5141 4.20775C22.5141 4.42244 22.6492 4.55125 22.8292 4.63712ZM26.9268 1.8892C27.4222 3.13435 28.0525 3.73546 29.3583 4.20775C28.0525 4.68006 27.4222 5.28116 26.9268 6.52631C26.4315 5.28116 25.8011 4.68006 24.4953 4.20775C25.8011 3.73546 26.4315 3.13435 26.9268 1.8892ZM48 22.6703C48 22.8851 47.8649 23.0568 47.6848 23.0997C46.0638 23.4862 45.8387 23.7438 45.3884 25.2895C45.3433 25.4612 45.1632 25.59 44.9381 25.59C44.7129 25.59 44.5328 25.4612 44.4878 25.2895C44.0826 23.7438 43.8124 23.5291 42.1914 23.0997C42.0112 23.0568 41.8762 22.8851 41.8762 22.6703C41.8762 22.4557 42.0112 22.2839 42.1914 22.241C43.8124 21.8546 44.0375 21.5969 44.4878 20.0512C44.5328 19.8795 44.7129 19.7507 44.9381 19.7507C45.1632 19.7507 45.3433 19.8795 45.3884 20.0512C45.7936 21.5969 46.0638 21.8116 47.6848 22.241C47.8649 22.2839 48 22.4557 48 22.6703ZM8.82552 18.3768C8.82552 18.162 8.69042 17.9903 8.5103 17.9474C5.98877 17.3033 5.53846 16.874 4.86304 14.4695C4.81801 14.2978 4.6379 14.169 4.41276 14.169C4.18762 14.169 4.0075 14.2978 3.96247 14.4695C3.28705 16.874 2.83678 17.3033 0.315197 17.9474C0.135084 17.9903 0 18.162 0 18.3768C0 18.5914 0.135084 18.7632 0.315197 18.8061C2.83678 19.4501 3.28705 19.8795 3.96247 22.2839C4.0075 22.4557 4.18762 22.5845 4.41276 22.5845C4.6379 22.5845 4.81801 22.4557 4.86304 22.2839C5.53846 19.8795 5.98877 19.4501 8.5103 18.8061C8.69042 18.7202 8.82552 18.5485 8.82552 18.3768ZM4.41276 20.6953C3.91745 19.4502 3.28706 18.849 1.98124 18.3768C3.28706 17.9044 3.91745 17.3033 4.41276 16.0582C4.90807 17.3033 5.53846 17.9044 6.84426 18.3768C5.53846 18.849 4.90807 19.4502 4.41276 20.6953ZM19.8124 16.4875C19.8124 16.5734 19.227 18.0762 17.606 18.0762C15.985 18.0762 15.3996 16.5734 15.3996 16.4875C15.3096 16.2729 15.4447 16.0152 15.6698 15.9294C15.8949 15.8435 16.1651 15.9723 16.2552 16.187C16.2552 16.2299 16.6154 17.1745 17.651 17.1745C18.6867 17.1745 19.0019 16.2299 19.0469 16.187C19.137 15.9723 19.3621 15.8435 19.6322 15.9294C19.7673 16.0152 19.9024 16.2729 19.8124 16.4875Z"
        fill="currentColor"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M26.8368 16.5305C27.4672 16.4017 29.8086 15.8006 31.2045 13.5249C32.1951 14.7271 34.8968 17.2604 39.8049 17.3892C39.2645 20.3089 36.6079 22.5845 33.3658 22.5845C29.8086 22.5845 26.9268 19.8795 26.8368 16.5305ZM33.3658 21.5111C34.9869 21.5111 35.5723 20.0083 35.5723 19.9225C35.6623 19.7078 35.5272 19.4501 35.3921 19.3643C35.1219 19.2784 34.8968 19.4072 34.8068 19.6219C34.7617 19.6648 34.4465 20.6094 33.4109 20.6094C32.3752 20.6094 32.015 19.6648 32.015 19.6219C31.9249 19.4072 31.6548 19.2784 31.4297 19.3643C31.2045 19.4501 31.0694 19.7078 31.1595 19.9225C31.1595 20.0083 31.7448 21.5111 33.3658 21.5111Z"
        fill="white"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M11.0769 13.5249C11.7073 13.3961 14.0488 12.795 15.4447 10.5194C16.4352 11.7216 19.137 14.2549 24.045 14.3837C23.5047 17.3033 20.848 19.5789 17.606 19.5789C14.0488 19.5789 11.167 16.874 11.0769 13.5249ZM17.606 18.0762C19.227 18.0762 19.8124 16.5734 19.8124 16.4875C19.9024 16.2729 19.7673 16.0152 19.6322 15.9294C19.3621 15.8435 19.137 15.9723 19.0469 16.187C19.0019 16.2299 18.6867 17.1745 17.651 17.1745C16.6154 17.1745 16.2552 16.2299 16.2552 16.187C16.1651 15.9723 15.8949 15.8435 15.6698 15.9294C15.4447 16.0152 15.3096 16.2729 15.3996 16.4875C15.3996 16.5734 15.985 18.0762 17.606 18.0762Z"
        fill="white"
      />
      <path
        d="M26.9268 1.8892C27.4222 3.13435 28.0525 3.73546 29.3583 4.20775C28.0525 4.68006 27.4222 5.28116 26.9268 6.52631C26.4315 5.28116 25.8011 4.68006 24.4953 4.20775C25.8011 3.73546 26.4315 3.13435 26.9268 1.8892Z"
        fill="white"
      />
      <path
        d="M4.41276 20.6953C3.91745 19.4502 3.28706 18.849 1.98124 18.3768C3.28706 17.9044 3.91745 17.3033 4.41276 16.0582C4.90807 17.3033 5.53846 17.9044 6.84426 18.3768C5.53846 18.849 4.90807 19.4502 4.41276 20.6953Z"
        fill="white"
      />
    </svg>
  ),

  Community: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      width="101"
      height="89"
      viewBox="0 0 101 89"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M90.0831 4.67378C91.3086 7.75422 92.8681 9.24133 96.0986 10.4098C92.8681 11.5782 91.3086 13.0653 90.0831 16.1458C88.8578 13.0653 87.2983 11.5782 84.0677 10.4098C87.2983 9.24133 88.8578 7.75422 90.0831 4.67378Z"
        fill="white"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M79.946 11.472C86.1842 13.0653 87.2983 14.1276 88.9692 20.0759C89.0806 20.5008 89.5262 20.8195 90.0831 20.8195C90.6402 20.8195 91.0858 20.5008 91.1972 20.0759C92.8681 14.1276 93.982 13.0653 100.22 11.472C100.666 11.3658 101 10.9409 101 10.4098C101 9.87866 100.666 9.45378 100.22 9.34755C93.982 7.75422 92.8681 6.692 91.1972 0.743558C91.0858 0.318669 90.6402 2.86102e-06 90.0831 2.86102e-06C89.5262 2.86102e-06 89.0806 0.318669 88.9692 0.743558C87.2983 6.692 86.1842 7.75422 79.946 9.34755C79.5005 9.45378 79.1663 9.87866 79.1663 10.4098C79.1663 10.9409 79.5005 11.2596 79.946 11.472ZM96.0986 10.4098C92.8681 9.24133 91.3086 7.75422 90.0831 4.67378C88.8578 7.75422 87.2983 9.24133 84.0677 10.4098C87.2983 11.5782 88.8578 13.0653 90.0831 16.1458C91.3086 13.0653 92.8681 11.5782 96.0986 10.4098Z"
        fill="currentColor"
      />
      <path
        d="M10.9169 68.9964C12.1423 72.0769 13.7019 73.564 16.9323 74.7324C13.7019 75.9009 12.1423 77.388 10.9169 80.4684C9.69155 77.388 8.13202 75.9009 4.90142 74.7324C8.13202 73.564 9.69155 72.0769 10.9169 68.9964Z"
        fill="white"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M0.77971 75.7946C7.01798 77.388 8.13202 78.4502 9.80295 84.3986C9.91436 84.8235 10.36 85.1421 10.9169 85.1421C11.4739 85.1421 11.9195 84.8235 12.0309 84.3986C13.7019 78.4502 14.8158 77.388 21.054 75.7946C21.4997 75.6884 21.8339 75.2635 21.8339 74.7324C21.8339 74.2013 21.4997 73.7764 21.054 73.6702C14.8158 72.0769 13.7019 71.0146 12.0309 65.0662C11.9195 64.6413 11.4739 64.3226 10.9169 64.3226C10.36 64.3226 9.91436 64.6413 9.80295 65.0662C8.13202 71.0146 7.01798 72.0769 0.77971 73.6702C0.334216 73.7764 0 74.2013 0 74.7324C0 75.2635 0.334216 75.5822 0.77971 75.7946ZM16.9323 74.7324C13.7019 73.564 12.1423 72.0769 10.9169 68.9964C9.69155 72.0769 8.13202 73.564 4.90142 74.7324C8.13202 75.9009 9.69155 77.388 10.9169 80.4684C12.1423 77.388 13.7019 75.9009 16.9323 74.7324Z"
        fill="currentColor"
      />
      <path
        d="M45.8757 24.3223C44.7499 25.5581 43.9077 26.4626 43.0826 27.376C40.1177 30.6446 39.5935 34.952 38.5708 39.0356C38.0122 41.2654 37.445 43.5131 37.1356 45.7877C36.5684 50.0414 38.9146 52.6115 43.0225 52.6115C43.8475 52.6115 44.9132 52.8801 45.4718 53.4533C51.1954 59.283 56.8416 65.2024 62.5051 71.1038C63.7426 72.3933 64.3785 74.0589 63.098 75.4649C62.316 76.3246 60.8121 76.7097 59.5917 76.8171C58.9472 76.8708 58.2167 75.9037 57.5292 75.3753C55.8963 74.1216 54.2634 72.85 52.3642 71.3903C52.3212 66.7247 51.0149 64.1367 45.1366 63.0621C43.4952 57.3398 42.2233 56.2293 37.4966 55.8711C35.8723 53.8025 34.7551 51.7339 33.0964 50.4175C30.5612 48.4115 27.1494 49.0295 24.64 51.4026C23.7978 52.2085 22.9641 53.0413 22.2165 53.9368C21.4688 54.8413 20.8328 55.8443 20.0078 56.9905C15.5475 52.701 12.531 48.3041 10.7779 43.0027C7.47778 33.0356 11.5857 22.4776 20.4375 18.3314C26.883 15.3135 33.1652 16.2538 39.181 19.6567C41.467 20.9552 43.5296 22.6746 45.8671 24.3223H45.8757Z"
        fill="currentColor"
      />
      <path
        d="M80.2257 53.8913C75.6967 49.0734 71.4599 44.5421 67.1973 40.0377C64.5417 37.2258 61.8604 34.4318 59.1619 31.6558C57.4689 29.9185 56.7985 29.9901 55.0196 31.6558C52.6133 33.9124 50.9718 36.5363 50.2929 39.9303C49.5538 43.6019 47.4654 46.4406 44.131 48.0346C43.3833 48.3928 42.2145 48.5003 41.5528 48.1242C41.0973 47.8645 40.934 46.5033 41.0973 45.7421C41.9051 41.8198 42.8247 37.9154 43.8044 34.0289C44.0537 33.0438 44.5005 32.005 45.1451 31.2528C50.7226 24.7603 56.6267 18.8052 65.2894 16.9067C77.3296 14.2739 88.6736 24.7424 87.5478 37.5213C86.9978 43.7093 84.4368 48.8316 80.2343 53.8913H80.2257Z"
        fill="currentColor"
      />
      <path
        d="M57.6923 34.9602C58.5431 36.1244 59.2565 37.3602 60.2018 38.3632C65.6762 44.1482 71.2021 49.8705 76.7194 55.6107C78.0515 56.9898 78.825 58.4853 77.7077 60.3658C76.5046 62.3807 74.3045 62.4882 72.431 60.5539C67.0942 55.0555 61.8003 49.5033 56.4892 43.978C53.4555 40.8169 53.5844 38.5781 57.6923 34.9602Z"
        fill="currentColor"
      />
      <path
        d="M48.188 50.5424C49.7693 48.7424 51.067 47.2827 52.4678 45.6887C53.0178 46.2081 53.5506 46.6738 54.0405 47.1842C59.0938 52.4409 64.147 57.7065 69.2003 62.9721C69.5011 63.2855 69.8105 63.5989 70.0855 63.9482C71.3144 65.5332 71.2715 67.2436 69.9909 68.5511C68.7448 69.8227 66.9572 69.9033 65.565 68.5242C62.6689 65.6496 59.8758 62.6676 57.0398 59.7214C54.1093 56.6856 51.1873 53.6498 48.188 50.5513V50.5424Z"
        fill="currentColor"
      />
      <path
        d="M38.7513 80.7036C36.0356 80.632 34.5747 77.6589 36.2333 75.6171C38.4763 72.85 41.0545 70.3515 43.6585 67.9426C44.8101 66.8859 46.6062 66.877 47.4828 68.274C48.1016 69.268 48.4367 71.3635 47.8695 72.0799C45.5577 74.9724 42.8764 77.5514 40.2553 80.1663C39.8428 80.5783 39.0693 80.5962 38.7513 80.6947V80.7036Z"
        fill="currentColor"
      />
      <path
        d="M31.412 73.1009C28.9026 73.083 27.2525 70.5218 28.7307 68.5696C30.9995 65.5696 33.7582 62.9368 36.5512 60.4294C37.1528 59.8832 39.0778 60.1339 39.8083 60.7518C40.5302 61.3697 41.1404 63.4741 40.7279 63.9756C38.1583 67.092 35.2965 69.9576 32.4863 72.8591C32.237 73.1188 31.6698 73.0471 31.4206 73.0919L31.412 73.1009Z"
        fill="currentColor"
      />
      <path
        d="M54.9855 79.189C54.3667 80.2994 54.0402 81.2307 53.4386 81.9113C51.8917 83.6665 50.2588 85.3501 48.5916 86.9799C46.9931 88.5381 45.1368 88.5381 43.8305 87.0963C42.6102 85.762 42.6617 83.8546 44.0797 82.3053C45.7556 80.4785 47.3884 78.5442 49.3049 77.0218C50.1471 76.3592 51.7714 76.2159 52.8198 76.5651C53.6878 76.8517 54.2206 78.2129 54.9769 79.189H54.9855Z"
        fill="currentColor"
      />
      <path
        d="M32.6926 55.8172C32.77 58.7903 26.3589 65.632 24.2447 65.3365C23.2736 65.2021 21.9845 64.4141 21.6322 63.5634C21.2368 62.6052 21.3228 60.9037 21.9158 60.1336C23.6002 57.9575 25.5424 55.9426 27.6394 54.1874C28.3785 53.5695 29.9597 53.5068 30.9309 53.856C31.8074 54.1784 32.366 55.4232 32.6926 55.8262V55.8172Z"
        fill="currentColor"
      />
    </svg>
  ),

  Education: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      width="37"
      height="34"
      viewBox="0 0 37 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g clip-path="url(#clip0_509_14058)">
        <path
          d="M35.9312 5.24496H33.9713V3.2781C33.9713 2.88473 33.71 2.62248 33.318 2.62248H31.3581V0.65562C31.3581 0.262248 31.0969 0 30.7048 0H9.14611C8.75414 0 8.49285 0.262248 8.49285 0.65562V6.62179C3.72378 6.9496 0 10.8833 0 15.7349C0 20.5865 3.72378 24.5202 8.49285 24.848V27.5361C8.49285 27.9294 8.75414 28.1917 9.14611 28.1917H11.106V30.1585C11.106 30.5519 11.3673 30.8141 11.7593 30.8141H13.7192V32.781C13.7192 33.1744 13.9805 33.4367 14.3725 33.4367H35.9312C36.3232 33.4367 36.5845 33.1744 36.5845 32.781V5.90058C36.5845 5.50721 36.3232 5.24496 35.9312 5.24496ZM8.49285 7.93302C8.68883 7.93302 8.95012 7.86742 9.14611 7.86742C9.34209 7.86742 9.60344 7.86742 9.79942 7.93302C11.2367 8.06415 12.6086 8.58864 13.7192 9.37538C14.1765 9.70319 14.5685 10.0965 14.9605 10.4899L15.0258 10.5555C15.3524 10.9489 15.6791 11.3422 15.9404 11.8012C16.267 12.3912 16.5937 13.0469 16.7244 13.768C16.855 14.1614 16.9203 14.6204 16.9203 15.0793C16.9203 15.276 16.9856 15.5382 16.9856 15.7349C16.9856 16.1938 16.9203 16.5872 16.855 17.0461C16.7897 17.5051 16.659 17.964 16.5284 18.3574C16.267 19.0786 15.9404 19.7342 15.4831 20.3242C15.3524 20.5209 15.1564 20.7176 15.0258 20.9143C14.8298 21.1765 14.5685 21.3732 14.3071 21.6355C14.1112 21.8322 13.9152 21.9633 13.7192 22.0944C12.6086 22.8811 11.2367 23.4057 9.79942 23.5368C9.60344 23.5368 9.34209 23.6023 9.14611 23.6023C8.95012 23.6023 8.68883 23.6023 8.49285 23.5368C4.50773 23.2089 1.30659 19.8653 1.30659 15.7349C1.30659 11.6045 4.50773 8.26083 8.49285 7.93302ZM9.79942 26.8804V24.848C11.2367 24.7169 12.5432 24.3235 13.7192 23.6679C14.1765 23.4057 14.6338 23.0779 15.0258 22.75C15.4178 22.4222 15.8097 22.0288 16.1364 21.6355H16.3324H26.7851C27.1771 21.6355 27.4384 21.3732 27.4384 20.9799C27.4384 20.5865 27.1771 20.3242 26.7851 20.3242H17.3777H17.051C17.3777 19.7342 17.7043 19.0786 17.9003 18.3574H18.9455H24.8252C25.2172 18.3574 25.4785 18.0951 25.4785 17.7017C25.4785 17.3083 25.2172 17.0461 24.8252 17.0461H19.9255H18.1616C18.2269 16.5872 18.2923 16.1938 18.2923 15.7349C18.2923 15.5382 18.2923 15.276 18.2269 15.0793H21.428H22.8653C23.2573 15.0793 23.5186 14.817 23.5186 14.4236C23.5186 14.0303 23.2573 13.768 22.8653 13.768H22.5387H18.0963C17.9656 13.0469 17.7043 12.3912 17.443 11.8012H24.1066H26.7851C27.1771 11.8012 27.4384 11.5389 27.4384 11.1456C27.4384 10.7521 27.1771 10.4899 26.7851 10.4899H25.0865H16.659C16.2017 9.83432 15.6791 9.24425 15.0258 8.71977C14.9605 8.65417 14.8951 8.58863 14.7645 8.52304H15.0258H24.8252C25.2172 8.52304 25.4785 8.26083 25.4785 7.86742C25.4785 7.47408 25.2172 7.21181 24.8252 7.21181H15.0258H13.7192H12.5432C11.694 6.884 10.7794 6.68733 9.79942 6.62179V1.31124H30.0516V26.8804H9.79942ZM35.2779 32.1254H15.0258V30.8141H33.318C33.71 30.8141 33.9713 30.5519 33.9713 30.1585V6.5562H35.2779V32.1254Z"
          fill="currentColor"
        />
        <path
          d="M8.49285 7.93302C8.68883 7.93302 8.95012 7.86742 9.14611 7.86742C9.34209 7.86742 9.60344 7.86742 9.79942 7.93302C11.2367 8.06415 12.6086 8.58864 13.7192 9.37538C14.1765 9.70319 14.5685 10.0965 14.9605 10.4899L15.0258 10.5555C15.3524 10.9489 15.6791 11.3422 15.9404 11.8012C16.267 12.3912 16.5937 13.0469 16.7244 13.768C16.855 14.1614 16.9203 14.6204 16.9203 15.0793C16.9203 15.276 16.9856 15.5382 16.9856 15.7349C16.9856 16.1938 16.9203 16.5872 16.855 17.0461C16.7897 17.5051 16.659 17.964 16.5284 18.3574C16.267 19.0786 15.9404 19.7342 15.4831 20.3242C15.3524 20.5209 15.1564 20.7176 15.0258 20.9143C14.8298 21.1765 14.5685 21.3732 14.3071 21.6355C14.1112 21.8322 13.9152 21.9633 13.7192 22.0944C12.6086 22.8811 11.2367 23.4057 9.79942 23.5368C9.60344 23.5368 9.34209 23.6023 9.14611 23.6023C8.95012 23.6023 8.68883 23.6023 8.49285 23.5368C4.50773 23.2089 1.30659 19.8653 1.30659 15.7349C1.30659 11.6045 4.50773 8.26083 8.49285 7.93302Z"
          fill="white"
        />
        <path
          d="M9.79942 26.8804V24.848C11.2367 24.7169 12.5432 24.3235 13.7192 23.6679C14.1765 23.4057 14.6338 23.0779 15.0258 22.75C15.4178 22.4222 15.8097 22.0288 16.1364 21.6355H16.3324H26.7851C27.1771 21.6355 27.4384 21.3732 27.4384 20.9799C27.4384 20.5865 27.1771 20.3242 26.7851 20.3242H17.3777H17.051C17.3777 19.7342 17.7043 19.0786 17.9003 18.3574H18.9455H24.8252C25.2172 18.3574 25.4785 18.0951 25.4785 17.7017C25.4785 17.3083 25.2172 17.0461 24.8252 17.0461H19.9255H18.1616C18.2269 16.5872 18.2923 16.1938 18.2923 15.7349C18.2923 15.5382 18.2923 15.276 18.2269 15.0793H21.428H22.8653C23.2573 15.0793 23.5186 14.817 23.5186 14.4236C23.5186 14.0303 23.2573 13.768 22.8653 13.768H22.5387H18.0963C17.9656 13.0469 17.7043 12.3912 17.443 11.8012H24.1066H26.7851C27.1771 11.8012 27.4384 11.5389 27.4384 11.1456C27.4384 10.7521 27.1771 10.4899 26.7851 10.4899H25.0865H16.659C16.2017 9.83432 15.6791 9.24425 15.0258 8.71977C14.9605 8.65417 14.8951 8.58863 14.7645 8.52304H15.0258H24.8252C25.2172 8.52304 25.4785 8.26083 25.4785 7.86742C25.4785 7.47408 25.2172 7.21181 24.8252 7.21181H15.0258H13.7192H12.5432C11.694 6.884 10.7794 6.68733 9.79942 6.62179V1.31124H30.0516V26.8804H9.79942ZM35.2779 32.1254H15.0258V30.8141H33.318C33.71 30.8141 33.9713 30.5519 33.9713 30.1585V6.5562H35.2779V32.1254Z"
          fill="white"
        />
        <path
          d="M35.2779 32.1254H15.0258V30.8141H33.318C33.71 30.8141 33.9713 30.5519 33.9713 30.1585V6.5562H35.2779V32.1254Z"
          fill="white"
        />
        <path
          d="M26.7856 3.93384H12.4131C12.0211 3.93384 11.7598 4.19609 11.7598 4.58946C11.7598 4.98283 12.0211 5.24508 12.4131 5.24508H14.373H26.7856C27.1775 5.24508 27.4388 4.98283 27.4388 4.58946C27.4388 4.19609 27.1775 3.93384 26.7856 3.93384Z"
          fill="currentColor"
        />
        <path
          d="M9.14568 20.9796C9.8643 20.9796 10.4523 20.3896 10.4523 19.6684V15.7347C10.4523 15.0135 9.8643 14.4234 9.14568 14.4234C8.42706 14.4234 7.83911 15.0135 7.83911 15.7347V19.6684C7.83911 20.3896 8.42706 20.9796 9.14568 20.9796Z"
          fill="currentColor"
        />
        <path
          d="M9.14568 13.1121C9.8643 13.1121 10.4523 12.522 10.4523 11.8009C10.4523 11.3419 10.191 10.883 9.799 10.6863C9.60295 10.5552 9.40697 10.4896 9.14568 10.4896C8.88433 10.4896 8.68835 10.5552 8.49237 10.6863C8.1004 10.883 7.83911 11.3419 7.83911 11.8009C7.83911 12.522 8.42706 13.1121 9.14568 13.1121Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_509_14058">
          <rect width="37" height="34" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),

  Conversation: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      width="34"
      height="33"
      viewBox="0 0 34 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g clip-path="url(#clip0_502_14046)">
        <path
          d="M31.1562 11.6564H24.8455V2.9128C24.8455 1.52176 23.7523 0.428802 22.3609 0.428802H2.48455C1.0932 0.428802 0 1.52176 0 2.9128V15.8296C0 17.2206 1.0932 18.3135 2.48455 18.3135H5.36662C5.9132 19.2575 5.76415 20.3504 4.91939 21.493C4.67096 21.8408 4.67096 22.2879 4.91939 22.6854C5.11815 22.9835 5.41629 23.1325 5.76415 23.1325C5.86353 23.1325 5.9132 23.1325 6.01258 23.0828C9.14311 22.2879 10.7829 19.3072 11.2302 18.3135H12.9196V25.8152C12.9196 27.1069 13.9632 28.1502 15.2551 28.1502H23.1063C23.5535 29.0941 25.0939 31.8264 27.9263 32.522C28.0257 32.522 28.0754 32.5717 28.1747 32.5717C28.4729 32.5717 28.771 32.4226 28.9698 32.1742C29.2183 31.8264 29.2183 31.3793 28.9698 31.0316C28.2244 29.9883 28.0754 29.0444 28.5723 28.1502H31.2059C32.4978 28.1502 33.5414 27.1069 33.5414 25.8152V13.9914C33.442 12.6997 32.3985 11.6564 31.1562 11.6564ZM10.932 17.32C10.7332 17.32 10.5345 17.4193 10.4848 17.618C10.4848 17.6677 8.94434 21.2943 5.76415 22.0892C6.90705 20.4995 7.00644 18.9097 6.11196 17.5187C5.96291 17.4193 5.81382 17.32 5.66477 17.32H2.48455C1.6398 17.32 0.99382 16.6741 0.99382 15.8296V2.9128C0.99382 2.06824 1.6398 1.4224 2.48455 1.4224H22.3609C23.2056 1.4224 23.8516 2.06824 23.8516 2.9128V15.8296C23.8516 16.6741 23.2056 17.32 22.3609 17.32H10.932ZM18.2863 21.4434C17.69 21.4434 17.1931 20.9466 17.1931 20.3504C17.1931 19.7543 17.69 19.2575 18.2863 19.2575C18.8825 19.2575 19.3794 19.7543 19.3794 20.3504C19.3794 20.9963 18.8825 21.4434 18.2863 21.4434ZM23.2056 21.4434C22.6094 21.4434 22.1125 20.9466 22.1125 20.3504C22.1125 19.7543 22.6094 19.2575 23.2056 19.2575C23.8019 19.2575 24.2988 19.7543 24.2988 20.3504C24.2492 20.9963 23.8019 21.4434 23.2056 21.4434ZM28.0754 21.4434C27.4791 21.4434 26.9822 20.9466 26.9822 20.3504C26.9822 19.7543 27.4791 19.2575 28.0754 19.2575C28.6717 19.2575 29.1686 19.7543 29.1686 20.3504C29.1686 20.9963 28.6717 21.4434 28.0754 21.4434Z"
          fill="currentColor"
        />
        <path
          d="M10.932 17.32C10.7332 17.32 10.5345 17.4193 10.4848 17.618C10.4848 17.6677 8.94434 21.2943 5.76415 22.0892C6.90705 20.4995 7.00644 18.9097 6.11196 17.5187C5.96291 17.4193 5.81382 17.32 5.66477 17.32H2.48455C1.6398 17.32 0.99382 16.6741 0.99382 15.8296V2.9128C0.99382 2.06824 1.6398 1.4224 2.48455 1.4224H22.3609C23.2056 1.4224 23.8516 2.06824 23.8516 2.9128V15.8296C23.8516 16.6741 23.2056 17.32 22.3609 17.32H10.932Z"
          fill="white"
        />
        <path
          d="M28.0754 21.4434C27.4791 21.4434 26.9822 20.9466 26.9822 20.3504C26.9822 19.7543 27.4791 19.2575 28.0754 19.2575C28.6717 19.2575 29.1686 19.7543 29.1686 20.3504C29.1686 20.9963 28.6717 21.4434 28.0754 21.4434Z"
          fill="white"
        />
        <path
          d="M23.2056 21.4434C22.6094 21.4434 22.1125 20.9466 22.1125 20.3504C22.1125 19.7543 22.6094 19.2575 23.2056 19.2575C23.8019 19.2575 24.2988 19.7543 24.2988 20.3504C24.2492 20.9963 23.8019 21.4434 23.2056 21.4434Z"
          fill="white"
        />
        <path
          d="M18.2863 21.4434C17.69 21.4434 17.1931 20.9466 17.1931 20.3504C17.1931 19.7543 17.69 19.2575 18.2863 19.2575C18.8825 19.2575 19.3794 19.7543 19.3794 20.3504C19.3794 20.9963 18.8825 21.4434 18.2863 21.4434Z"
          fill="white"
        />
        <path
          d="M12.423 12.4019H12.0752C11.628 12.4019 11.2802 12.7497 11.2802 13.1968V13.5446C11.2802 13.9917 11.628 14.3395 12.0752 14.3395H12.423C12.8702 14.3395 13.2181 13.9917 13.2181 13.5446V13.1968C13.2181 12.7497 12.8702 12.4019 12.423 12.4019Z"
          fill="currentColor"
        />
        <path
          d="M12.5711 3.95615C10.6828 3.95615 9.53997 4.90004 9.09274 6.19176C8.94364 6.68855 9.2915 7.23502 9.83812 7.23502H9.98717C10.335 7.23502 10.6332 7.03629 10.7325 6.68855C10.9313 5.94335 11.5773 5.44656 12.5214 5.44656C13.4655 5.44656 14.0618 5.94335 14.0618 6.68855C14.0618 7.53308 13.3662 7.93054 12.7202 8.47701C12.0742 9.02348 11.7264 9.47059 11.5773 10.1164C11.4779 10.6133 11.8257 11.0604 12.3226 11.0604C12.6705 11.0604 12.9686 10.8119 13.068 10.4642C13.1177 10.2655 13.2171 10.1164 13.3165 9.96742C13.5649 9.66932 14.0121 9.37127 14.7078 8.82475C15.3041 8.32796 15.8507 7.68213 15.8507 6.63887C15.9004 5.04909 14.6084 3.95615 12.5711 3.95615Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_502_14046">
          <rect width="34" height="33" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),

  ArrowRight: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      width="26"
      height="16"
      viewBox="0 0 26 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M22.175 9H0V7H22.175L16.575 1.4L18 0L26 8L18 16L16.575 14.6L22.175 9Z"
        fill="currentColor"
      />
    </svg>
  ),

  Message: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g clip-path="url(#clip0_119_5262)">
        <path
          d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16ZM7 9H9V11H7V9ZM11 9H13V11H11V9ZM15 9H17V11H15V9Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_119_5262">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),

  Email: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M3.44444 6.28571V17.7143H20.5556V6.28571H3.44444ZM20.5556 4C21.9 4 23 5.02857 23 6.28571V17.7143C23 18.9714 21.9 20 20.5556 20H3.44444C2.1 20 1 18.9714 1 17.7143V6.28571C1 5.02857 2.1 4 3.44444 4H20.5556Z"
        fill="currentColor"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M4.05311 5.71429H1.15156C1.33367 6.21714 1.63922 6.69029 2.06822 7.09143L9.25733 13.8126C9.61114 14.1437 10.0313 14.4064 10.4937 14.5856C10.9561 14.7648 11.4518 14.857 11.9523 14.857C12.4529 14.857 12.9486 14.7648 13.411 14.5856C13.8734 14.4064 14.2935 14.1437 14.6473 13.8126L21.8364 7.09143C22.2642 6.69029 22.5698 6.21714 22.7519 5.71429H19.8516L12.9191 12.1966C12.7921 12.3154 12.6413 12.4097 12.4753 12.474C12.3093 12.5383 12.1314 12.5714 11.9517 12.5714C11.772 12.5714 11.5941 12.5383 11.4281 12.474C11.2622 12.4097 11.1113 12.3154 10.9843 12.1966L4.05311 5.71429Z"
        fill="currentColor"
      />
    </svg>
  ),

  Clinic: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="31"
      height="27"
      fill="none"
      viewBox="0 0 31 27"
      {...props}
    >
      <path
        fill="#fff"
        fillOpacity="0.5"
        stroke="currentColor"
        strokeWidth="1.5"
        d="M2.096 1.75h26.308c.191 0 .346.155.346.346v17.539c0 .167-.179.345-.346.345H2.096c-.167 0-.346-.178-.346-.345V2.095c0-.166.179-.345.346-.345ZM16.935 21.48l1.592 3.98h-6.553l1.592-3.98z"
      ></path>
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M28.404 1H2.096C1.491 1 1 1.49 1 2.096v17.539c0 .605.49 1.096 1.096 1.096h26.308c.605 0 1.096-.491 1.096-1.096V2.096C29.5 1.491 29.01 1 28.404 1M13.058 20.73l-2.193 5.481M17.442 20.73l2.193 5.481M8.673 26.21h13.154"
      ></path>
      <path
        fill="#fff"
        stroke="currentColor"
        strokeWidth="1.5"
        d="M13.721 5.56h3.06c.106 0 .191.086.191.192v3.397h3.398c.105 0 .191.086.191.192v3.06a.19.19 0 0 1-.191.19h-3.398v3.398a.19.19 0 0 1-.191.192h-3.06a.19.19 0 0 1-.191-.192v-3.397h-3.397a.19.19 0 0 1-.192-.192V9.34c0-.105.086-.19.192-.19h3.397V5.751c0-.106.086-.191.191-.191Z"
      ></path>
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M13.72 4.803a.94.94 0 0 0-.941.941v2.648h-2.647a.94.94 0 0 0-.942.941v3.06c0 .52.422.94.942.94h2.647v2.648c0 .52.421.941.941.941h3.06c.52 0 .941-.42.941-.94v-2.648h2.648c.52 0 .94-.422.94-.942V9.333a.94.94 0 0 0-.94-.941H17.72V5.744a.94.94 0 0 0-.941-.941z"
      ></path>
    </svg>
  ),

  CheckCircle: ({ className, ...props }: SVGProps) => (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="27"
      height="26"
      fill="none"
      viewBox="0 0 27 26"
      {...props}
    >
      <circle
        cx="13.5"
        cy="13"
        r="13"
        fill="currentColor"
        fillOpacity="0.2"
      ></circle>
      <path
        fill="currentColor"
        d="M13.5 5a8 8 0 1 0 8 8 8.01 8.01 0 0 0-8-8m3.512 6.59-4.307 4.307a.616.616 0 0 1-.871 0L9.988 14.05a.616.616 0 0 1 .87-.871l1.411 1.412 3.873-3.873a.615.615 0 1 1 .87.87"
      />
    </svg>
  ),

  TriangleShape: ({ className, ...props }: SVGProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="7"
      height="5"
      fill="none"
      viewBox="0 0 7 5"
      {...props}
      className={className}
    >
      <path
        fill="currentColor"
        d="M3.712 4.9 6.966.3a.17.17 0 0 0 .002-.199A.27.27 0 0 0 6.754 0H.246a.27.27 0 0 0-.214.101A.17.17 0 0 0 .034.3l3.254 4.6c.043.062.124.1.212.1s.17-.038.212-.1"
      ></path>
    </svg>
  ),

  QuoteMarkIcon: ({ className, ...props }: SVGProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="14"
      fill="none"
      viewBox="0 0 19 14"
      {...props}
      className={className}
    >
      <path
        fill="currentColor"
        d="M7.04 0L7.12 0.240002C4.96 1.04 2.24 3.04 2.24 5.16C2.24 6.68 3.76 6.96 5.44 7.28C7.56 7.68 8.56 9.16 8.56 10.64C8.56 12.2 7.36 13.76 5.12 13.76C2.72 13.76 0 11.96 0 8C0 3 4 0.840001 7.04 0ZM16.8 0L16.88 0.240002C14.72 1.04 12 3.04 12 5.16C12 6.68 13.52 6.96 15.2 7.28C17.32 7.68 18.32 9.16 18.32 10.64C18.32 12.2 17.12 13.76 14.88 13.76C12.48 13.76 9.76 11.96 9.76 8C9.76 3 13.76 0.840001 16.8 0Z"
      />
    </svg>
  ),
};

export const IconWrapper = ({
  size = "md",
  className,
  style,
  children,
}: {
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) => (
  <div
    className={tw(
      "item-center flex flex-row",
      size === "sm" && "h-5 w-5",
      size === "md" && "h-6 w-6",
      size === "lg" && "h-7 w-7",
      size === "xl" && "h-10 w-10",
      className,
    )}
    style={style}
  >
    {children}
  </div>
);
